import { useState, useEffect } from "react";
import { Search, Settings } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

interface Configuration {
  id: string;
  name: string;
  description: string | null;
  script_type: string;
  created_at: string;
  updated_at: string;
}

interface TagConfigurationsTabProps {  
  tagId: string;
}

export const TagConfigurationsTab = ({ tagId }: TagConfigurationsTabProps) => {
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        setIsLoading(true);

        const { data, error } = await supabase
          .from('configuration_tags')
          .select(`
            configuration_id,
            configurations (
              id,
              name,
              description,
              script_type,
              created_at,
              updated_at
            )
          `)
          .eq('tag_id', tagId);

        if (error) {
          console.error('Error fetching configurations:', error);
          return;
        }

        const configurationList = data?.map(item => item.configurations).filter(Boolean) as Configuration[];
        setConfigurations(configurationList || []);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigurations();
  }, [tagId]);

  const filteredConfigurations = configurations.filter(config =>
    config.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    config.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search configurations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {filteredConfigurations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Settings className="h-12 w-12 text-muted-foreground mb-4" />
            <CardTitle className="text-center mb-2">No Configurations Found</CardTitle>
            <CardDescription className="text-center">
              {searchQuery 
                ? "No configurations match your search criteria."
                : "This tag has no configurations assigned to it yet."
              }
            </CardDescription>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredConfigurations.map((configuration) => (
            <Card key={configuration.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-base">
                      <Link 
                        to={`/configurations/${configuration.id}`}
                        className="hover:text-primary underline-offset-4 hover:underline"
                      >
                        {configuration.name}
                      </Link>
                    </CardTitle>
                    {configuration.description && (
                      <CardDescription className="text-sm line-clamp-2">
                        {configuration.description}
                      </CardDescription>
                    )}
                  </div>
                  <Badge variant="outline">
                    {configuration.script_type}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type:</span>
                    <span className="capitalize">{configuration.script_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created:</span>
                    <span>{new Date(configuration.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Updated:</span>
                    <span>{new Date(configuration.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};